import PageHeader from '@/components/feature/PageHeader';
import { useSEO } from '@/hooks/useSEO';
import { useFormSubmit } from '@/hooks/useFormSubmit';

const FEEDBACK_URL = 'https://readdy.ai/api/form/db7501raskivmpnrcfvg';
const MAX_LENGTH = 500;

const values = [
  { icon: 'ri-flashlight-line', title: '快速聚合', desc: '把分散的娱乐资讯、视频与话题聚在一处，减少你在多个平台之间来回切换。' },
  { icon: 'ri-chat-smile-2-line', title: '鼓励讨论', desc: '每一个话题都欢迎不同声音，希望这里是一个能聊起来、能被回应的社区。' },
  { icon: 'ri-compass-3-line', title: '精选发现', desc: '通过专题与推荐，帮你越过算法，发现那些被低估但值得一看的内容。' },
];

export default function AboutPage() {
  const { status, message, submit } = useFormSubmit(FEEDBACK_URL, '提交成功，感谢你的反馈，我们会认真阅读每一条建议！');

  useSEO(
    '关于我们 - 一起艹',
    '了解一起艹的定位、发展愿景与内容方向。我们致力于成为娱乐爱好者的内容聚合与交流平台，欢迎联系与反馈。',
    '关于我们,一起艹,娱乐平台,联系方式,反馈',
  );

  return (
    <div className="w-full">
      <PageHeader
        title="关于一起艹"
        subtitle="一个属于娱乐爱好者的内容聚合平台，我们相信好的内容值得被认真对待。"
        crumbs={[{ label: '首页', to: '/' }, { label: '关于我们' }]}
        icon="ri-information-line"
      />

      <div className="w-full max-w-[1280px] mx-auto px-4 md:px-6 py-8 md:py-12">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-8 md:gap-12">
          <div className="lg:col-span-7">
            <section>
              <h2 className="font-heading text-xl md:text-2xl text-foreground-950 mb-3">我们是谁</h2>
              <p className="text-sm md:text-base text-foreground-700 leading-loose">
                一起艹是一个面向年轻群体与娱乐爱好者的综合娱乐内容平台。我们把娱乐新闻、精选视频与互动话题
                聚合在一起，让你用更短的时间了解正在发生的事，也更容易找到同好。
              </p>
              <p className="mt-4 text-sm md:text-base text-foreground-700 leading-loose">
                我们不追求把所有内容都堆在首页，而是希望通过分类、筛选、专题与搜索，帮助每一位用户在需要的时候
                快速找到想看的内容。
              </p>
            </section>

            <section className="mt-8">
              <h2 className="font-heading text-xl md:text-2xl text-foreground-950 mb-4">我们在意的事</h2>
              <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
                {values.map((value) => (
                  <div key={value.title} className="p-5 rounded-lg border border-background-200 bg-background-50">
                    <span className="w-11 h-11 flex items-center justify-center rounded-lg bg-primary-100 text-primary-700">
                      <i className={`${value.icon} text-xl`} />
                    </span>
                    <h3 className="mt-3.5 font-medium text-base text-foreground-950">{value.title}</h3>
                    <p className="mt-2 text-sm text-foreground-500 leading-relaxed">{value.desc}</p>
                  </div>
                ))}
              </div>
            </section>

            <section className="mt-8">
              <h2 className="font-heading text-xl md:text-2xl text-foreground-950 mb-3">发展愿景</h2>
              <p className="text-sm md:text-base text-foreground-700 leading-loose">
                我们希望一起艹不只被「浏览」，更能被「参与」。未来会持续丰富视频与话题内容，
                完善专题策划，让每一个打开网站的人，都能遇见一点属于自己的娱乐时光。
              </p>
            </section>

            <section className="mt-8 p-5 md:p-6 rounded-lg bg-background-100 border border-background-200">
              <h2 className="font-heading text-xl md:text-2xl text-foreground-950 mb-3">联系我们</h2>
              <ul className="space-y-3 text-sm text-foreground-700">
                <li className="flex items-center gap-2.5">
                  <i className="ri-mail-line text-primary-600" />
                  联系邮箱：hello@yiqicao.demo
                </li>
                <li className="flex items-center gap-2.5">
                  <i className="ri-map-pin-line text-primary-600" />
                  办公地点：线上运营，欢迎邮件联系
                </li>
                <li className="flex items-center gap-2.5">
                  <i className="ri-time-line text-primary-600" />
                  内容更新：每周持续更新
                </li>
              </ul>
            </section>
          </div>

          <div className="lg:col-span-5">
            <div className="p-5 md:p-6 rounded-lg border border-background-200 bg-background-50 lg:sticky lg:top-24">
              <h2 className="font-heading text-xl md:text-2xl text-foreground-950">意见与反馈</h2>
              <p className="mt-2 text-sm text-foreground-500">
                对内容、功能或体验有任何建议，都可以通过下面的表单告诉我们。
              </p>

              <form
                id="feedback-form"
                data-readdy-form
                onSubmit={(event) => {
                  event.preventDefault();
                  submit(event.currentTarget);
                }}
                className="mt-5 space-y-4"
              >
                <input
                  type="text"
                  name="company_alt"
                  className="form-honeypot-field"
                  tabIndex={-1}
                  autoComplete="off"
                  aria-hidden="true"
                  readOnly
                />
                <div>
                  <label htmlFor="fb-name" className="block text-xs text-foreground-500 mb-1.5">称呼</label>
                  <input
                    id="fb-name"
                    type="text"
                    name="name"
                    required
                    placeholder="怎么称呼你？"
                    className="w-full h-11 px-4 rounded-md border border-background-200 bg-background-50 text-sm text-foreground-950 placeholder:text-foreground-400 outline-none focus:border-primary-400"
                  />
                </div>
                <div>
                  <label htmlFor="fb-email" className="block text-xs text-foreground-500 mb-1.5">邮箱</label>
                  <input
                    id="fb-email"
                    type="email"
                    name="email"
                    required
                    placeholder="用于接收回复的邮箱"
                    className="w-full h-11 px-4 rounded-md border border-background-200 bg-background-50 text-sm text-foreground-950 placeholder:text-foreground-400 outline-none focus:border-primary-400"
                  />
                </div>
                <div>
                  <label htmlFor="fb-type" className="block text-xs text-foreground-500 mb-1.5">反馈类型</label>
                  <select
                    id="fb-type"
                    name="feedback_type"
                    defaultValue="内容建议"
                    className="w-full h-11 px-4 rounded-md border border-background-200 bg-background-50 text-sm text-foreground-950 outline-none focus:border-primary-400"
                  >
                    <option value="内容建议">内容建议</option>
                    <option value="功能反馈">功能反馈</option>
                    <option value="内容纠错">内容纠错</option>
                    <option value="合作咨询">合作咨询</option>
                  </select>
                </div>
                <div>
                  <label htmlFor="fb-message" className="block text-xs text-foreground-500 mb-1.5">反馈内容</label>
                  <textarea
                    id="fb-message"
                    name="message"
                    required
                    rows={4}
                    maxLength={MAX_LENGTH}
                    placeholder="请详细描述你的建议或问题（最多 500 字）"
                    className="w-full resize-none rounded-md border border-background-200 bg-background-50 px-4 py-3 text-sm text-foreground-950 placeholder:text-foreground-400 outline-none focus:border-primary-400"
                  />
                </div>
                <button
                  type="submit"
                  disabled={status === 'submitting'}
                  className="w-full h-11 rounded-md bg-primary-500 text-background-50 text-sm font-medium hover:bg-primary-600 whitespace-nowrap cursor-pointer disabled:opacity-60 transition-colors"
                >
                  {status === 'submitting' ? '提交中…' : '提交反馈'}
                </button>
                {message ? (
                  <p className={`text-sm ${status === 'success' ? 'text-accent-700' : 'text-primary-600'}`}>{message}</p>
                ) : null}
              </form>
            </div>
          </div>
        </div>

        <section className="mt-10 md:mt-14 p-5 md:p-6 rounded-lg border border-dashed border-background-300 bg-background-50">
          <h2 className="font-heading text-lg text-foreground-950 mb-2">版权信息与免责声明</h2>
          <p className="text-xs md:text-sm text-foreground-500 leading-relaxed">
            本站为演示性质的内容聚合平台，站内所有资讯标题、正文、视频与话题均为虚构演示数据，
            不对应任何真实人物或机构，也不包含真实版权素材。视频播放区域当前使用示例视频，仅用于演示播放器功能。
            本站不对内容的时效性、准确性作出承诺，也不构成任何形式的业务完成声明。
          </p>
        </section>
      </div>
    </div>
  );
}